import { useState, useRef, useEffect, useMemo, useContext } from 'react'
import { useRouteMatch, useLocation } from 'react-router-dom'
import { DesignContext } from 'contexts/DesignContext'
import FeatherIcon from 'feather-icons-react'
import { Form, Row, Col, Button } from 'react-bootstrap'
import { Pagination } from '@mui/material'
import PerfectScrollbar from 'react-perfect-scrollbar'
import objectPath from 'object-path'
import PNotify from 'pnotify/dist/es/PNotify'
import httpClient from 'utility/httpClient.util'
import moment from 'moment'
import './score.css'
import AddScore from './single/AddScore'
import ShowScore from './single/ShowScore'
import Label from '../Label'
import { checkCustomValidation, checkApiValidation } from './Validation'
import { setApi } from 'designer/component/action/Api'
import { getUrlParamValue } from 'designer/component/action/Data'
import EditRowScore from './single/EditRowScore'
import useAuth from 'hooks/useAuth'
import { getScoreVisibleConfig } from 'designer/component/action/Config'

const SCORE_CONDITION_CREATE_ONLY = 'createOnly'
const SCORE_CONDITION_CRUD = 'crud'

const SingleScore = (props) => {
  const { uischema, config = {}, isReadOnly } = props
  const {
    isDynamic = false,
    dynamicName = '',
    dynamicIndex = 0,
    dynamicData = {},
    onUpdateFormData = () => {},
    isArray,
    arrayName,
  } = config

  const fieldKey = isArray
    ? `@${arrayName}.${uischema.key}`
    : isDynamic
    ? `${dynamicName}.${dynamicIndex}.${uischema.key}`
    : uischema.key

  const match = useRouteMatch()
  const location = useLocation()
  const search = location.search || []
  const receivePathParams = match.params
  const urlSearchParams = new URLSearchParams(search)
  const receiveQueryParams = Object.fromEntries([...urlSearchParams])
  const allParams = { ...receivePathParams, ...receiveQueryParams }
  const designContext = useContext(DesignContext)
  const { jsonForm, setFormData, setDatasourceValue, elementAction } =
    designContext
  const { config: accountConfig } = useAuth()
  let datasourceValue = objectPath.get(designContext, '@datasource') || {}
  const formData = objectPath.get(jsonForm, 'formData') || {}
  const formGroup = objectPath.get(jsonForm, 'formGroup') || []
  const designerPropsData = objectPath.get(jsonForm, 'designerPropsData') || {}
  const designerData = objectPath.get(designerPropsData, 'data') || {}

  const [page, setPage] = useState(1)
  const [scoreList, setScoreList] = useState([])
  const [warningList, setWarningList] = useState([])
  const [isAddScore, setIsAddScore] = useState(false)
  const [isEditScore, setIsEditScore] = useState(null)

  const scoreConfig = useMemo(
    () =>
      getScoreVisibleConfig({
        uischema,
        config: accountConfig,
        jsonForm,
      }),
    [uischema, accountConfig, jsonForm],
  )
  const scoreUischema = useMemo(
    () => ({ ...uischema, scoreConfig }),
    [uischema, scoreConfig],
  )
  const scoreSetting = objectPath.get(uischema, 'scoreSetting')
  const scoreAction = objectPath.get(uischema, 'scoreAction') || {}
  const { editOnRow } = scoreAction
  const scoreActionCreate = objectPath.get(scoreAction, 'create') || {}
  const scoreActionCreateOnly = objectPath.get(scoreAction, 'createOnly') || {}
  const scoreActionUpdate = objectPath.get(scoreAction, 'update') || {}
  const scoreActionDelete = objectPath.get(scoreAction, 'delete') || {}
  const scoreActionFetch = objectPath.get(scoreAction, 'fetch') || {}
  const conditionCreateOnly = objectPath.get(
    scoreAction,
    `condition.${SCORE_CONDITION_CREATE_ONLY}`,
  )
  const conditionCrud = objectPath.get(
    scoreAction,
    `condition.${SCORE_CONDITION_CRUD}`,
  )
  const scoreType = useMemo(() => {
    if (conditionCreateOnly) {
      let conditionValue = objectPath.has(
        formData,
        `@condition.${conditionCreateOnly}`,
      )
      if (conditionValue) return SCORE_CONDITION_CREATE_ONLY
    }
    if (conditionCrud) {
      let conditionValue = objectPath.has(
        formData,
        `@condition.${conditionCrud}`,
      )
      if (conditionValue) return SCORE_CONDITION_CRUD
    }
    return scoreAction.scoreType
  }, [scoreAction.scoreType])

  const scoreChartValue = objectPath.get(formData, uischema.key) ?? null

  const scoreNameStyle = useMemo(() => {
    if (scoreSetting?.nameWidth) {
      return {
        width: scoreSetting.nameWidth,
      }
    }
    return {}
  }, [scoreSetting?.nameWidth])

  const count = useMemo(() => {
    if (scoreSetting?.pagination > 0) {
      let count = scoreList.length / scoreSetting?.pagination
      let paginationCount = Math.ceil(count)
      return paginationCount
    }
    return 0
  }, [scoreList.length, scoreSetting?.pagination])

  const [scoreChunk, setScoreChunk] = useState([])
  const [scoreChunkList, setScoreChunkList] = useState([])
  const [isUpdateChunk, setIsUpdateChunk] = useState(false)

  const dateChart = useMemo(() => {
    return scoreConfig.find(({ isDate, enabled }) => isDate && enabled) || {}
  }, [scoreConfig])

  const timeChart = useMemo(() => {
    return scoreConfig.find(({ isTime, enabled }) => isTime && enabled) || {}
  }, [scoreConfig])

  const warningChart = useMemo(() => {
    let warning = {}
    scoreConfig.forEach(({ validationType, validation, name }) => {
      if (['api', 'custom'].includes(validationType) && validation.length > 0) {
        objectPath.set(warning, name, { validation, validationType })
      }
    })
    return warning
  }, [scoreConfig])

  const checkScoreChunk = (scoreList) => {
    let pagination = scoreSetting?.pagination || 1
    let result = scoreList.reduce((resultArray, item, index) => {
      const chunkIndex = Math.floor(index / pagination)
      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = [] // start a new chunk
      }
      resultArray[chunkIndex].push(item)
      return resultArray
    }, [])
    let index = page - 1
    setScoreChunkList(result)
    setScoreChunk(result[index])
  }

  useEffect(() => checkScoreChunk(scoreList), [count, page])

  useEffect(() => {
    if (isUpdateChunk) {
      setIsUpdateChunk(false)
      checkScoreChunk(scoreList)
    }
  }, [isUpdateChunk])

  const { controlId, label } = uischema

  const addScore = () => {
    if (editOnRow) {
      let score = checkFormatScore({})
      storeCreateScore(score)
    } else {
      setIsAddScore(!isAddScore)
    }
  }

  const editScore = (index) => setIsEditScore(index)

  const cancelScore = () => {
    setIsAddScore(false)
    setIsEditScore(null)
  }

  const checkFormatScore = (score) => {
    scoreConfig.forEach(
      ({ group, name, data, enabled, isDate, isTime, times }) => {
        if (!enabled) return
        if (group) {
          data.forEach(({ name, enabled }) => {
            if (!enabled) return
            let checked = objectPath.has(score, name)
            if (!checked) {
              objectPath.set(score, name, '')
            }
          })
        } else {
          let checked = objectPath.has(score, name)
          let value = ''
          if (isDate) {
            const dateName = objectPath.get(dateChart, 'name')
            value =
              objectPath.get(score, dateName) || moment().format('YYYY-MM-DD')
          }
          if (isDate) {
            const timeName = objectPath.get(timeChart, 'name')
            value = objectPath.get(score, timeName)
          }
          if (!checked) {
            objectPath.set(score, name, value)
          }
        }
      },
    )
    return score
  }

  const saveScore = async (index, score) => {
    score = checkFormatScore(score)
    new Promise(async (resolve) => {
      if (index === -1) {
        if (scoreActionCreate.isOnAPI) {
          console.log('add by api', score)
          let response = await callAPI(score, scoreActionCreate)
          resolve(response)
        } else {
          resolve(score)
        }
      } else {
        if (scoreActionUpdate.isOnAPI) {
          console.log('update by api', score)
          let response = await callAPI(score, scoreActionUpdate)
          resolve(response)
        } else {
          resolve(score)
        }
      }
    }).then(async (response) => {
      console.log('call fetch', response)
      if (scoreActionFetch.isEnabled) {
        await callAPI(score, scoreActionFetch)
      } else {
        if (index === -1) {
          storeCreateScore(score)
        } else {
          storeUpdateScore(index, score)
        }
      }
    })
    setIsAddScore(false)
    setIsEditScore(null)
  }

  const deleteScore = async (index) => {
    let scores = []
    if (count > 0) {
      let chunkIndex = page - 1
      scores = objectPath.get(scoreChunkList, [chunkIndex, index])
    } else {
      scores = objectPath.get(scoreList, index)
    }
    new Promise(async (resolve) => {
      if (scoreActionDelete.isOnAPI) {
        let response = await deleteAPI(scores, scoreActionDelete)
        resolve(response)
      } else {
        let chunkIndex = page - 1
        objectPath.del(scoreChunkList, [chunkIndex, index])
        updateScoreChunk(scoreChunkList)
        resolve(true)
      }
    }).then((response) => {
      console.log('call fetch', response)
      if (scoreActionFetch.isEnabled) {
        setTimeout(async () => {
          await callAPI(scores, scoreActionFetch)
        }, 500)
      }
    })
    setRender(true)
  }

  const updateScoreToFormData = (scoreList) => {
    checkScoreChunk(scoreList)
    objectPath.set(formData, uischema.key, scoreList)
    objectPath.set(formData, uischema.name, scoreList)
    setFormData(formData)
  }

  const storeCreateScore = (scores) => {
    let index = scoreList.length
    objectPath.push(scoreList, '', scores)
    setScoreList(scoreList)
    checkWarning(index, scoreList)
    updateScoreToFormData(scoreList)
  }

  const storeUpdateScore = (index, scores) => {
    objectPath.set(scoreList, index, scores)
    setScoreList(scoreList)
    checkWarning(index, scoreList)
    updateScoreToFormData(scoreList)
  }

  const updateScoreChunk = (scoreChunkList) => {
    let scoreList = []
    scoreChunkList.forEach((chunk) => {
      scoreList = scoreList.concat(chunk)
    })
    setScoreList(scoreList)
    updateScoreToFormData(scoreList)
  }

  const isNumber = (a) => Number(a) === +a

  const setRequestFormData = (form, requestBody, scores) => {
    let { jsonKey = '', mapParam = {}, mapOther = {} } = form
    let paramList = Object.keys(mapParam)
    paramList.forEach((key) => {
      let name = mapParam[key]
      let requestKey = jsonKey ? `${jsonKey}.${name}` : name
      if (uischema.key === key) {
        objectPath.set(requestBody, requestKey, scores)
      } else {
        let value =
          objectPath.get(formData, key) || objectPath.get(formData, name)
        objectPath.set(requestBody, requestKey, value)
      }
    })
    let otherList = Object.values(mapOther)
    otherList.forEach(({ name, value, dataType = '', jsonKey }) => {
      let dataKey = jsonKey ? `${jsonKey}.${name}` : name
      if (dataType === 'prop' || dataType === 'system') {
        let value = objectPath.get(formData, dataKey)
        objectPath.set(requestBody, name, value)
      } else if (dataType === 'urlParam') {
        let urlParamValue = getUrlParamValue(allParams, value, dataKey)
        objectPath.set(requestBody, name, urlParamValue)
      } else if (dataType === 'custom') {
        if (isNumber(value)) {
          objectPath.set(requestBody, name, Number(value))
        } else {
          objectPath.set(requestBody, name, value)
        }
      } else if (dataType === 'dateTime') {
        let now = null
        if (value === 'currentDate') {
          now = moment().format('YYYY-MM-DD')
        } else if (value === 'currentTime') {
          now = moment().format('HH:mm:ss')
        } else if (value === 'currentDateTime') {
          now = moment().format('YYYY-MM-DD HH:mm:ss')
        }
        objectPath.set(requestBody, name, now)
      } else if (dataType === 'datasource') {
        let dataValue = objectPath.get(datasourceValue, value)
        objectPath.set(requestBody, name, dataValue)
      }
    })
    return requestBody
  }

  const setRequestData = (action, formData, scores) => {
    let requestData = {}
    let { requestDataType } = action
    if (requestDataType === 'customData') {
      let { customData = [] } = action
      customData.forEach(
        ({ request, propType, value, key = '', isDynamic = false }) => {
          if (uischema.key === key) {
            objectPath.set(requestData, request, scores)
          } else if (isDynamic) {
            let value = objectPath.get(formData, request)
            objectPath.set(requestData, request, value)
          } else if (propType === 'input') {
            let value = objectPath.has(formData, key)
              ? objectPath.get(formData, key)
              : objectPath.get(formData, request)
            objectPath.set(requestData, request, value)
          } else if (propType === 'system') {
            let value = objectPath.get(formData, key)
            objectPath.set(requestData, request, value)
          } else if (propType === 'custom') {
            objectPath.set(requestData, request, value)
          } else if (propType === 'dateTime') {
            let now = null
            if (value === 'currentDate') {
              now = moment().format('YYYY-MM-DD')
            } else if (value === 'currentTime') {
              now = moment().format('HH:mm:ss')
            } else if (value === 'currentDateTime') {
              now = moment().format('YYYY-MM-DD HH:mm:ss')
            }
            objectPath.set(requestData, request, now)
          }
        },
      )
    } else if (requestDataType === 'formData') {
      let formDataList = action?.formDataList
      if (formDataList) {
        formDataList.forEach(({ key }) => {
          let form = formGroup.find((form) => form.key === key)
          if (!form) return requestData
          if (form.isGroup) {
            let groupData = form.groupData || []
            groupData.forEach((subGroup) => {
              requestData = setRequestFormData(subGroup, requestData, scores)
            })
          } else {
            requestData = setRequestFormData(form, requestData, scores)
          }
        })
      }
    }
    console.log('requestData', requestData)
    return requestData
  }

  const getData = async (action) => {
    let {
      responseDataType,
      customName,
      dataKey = '',
      dataTable,
      headers = [],
    } = action

    let api = setApi(action, formData, {
      allParams,
      designerData,
      datasourceValue,
    })
    if (!api) return {}
    // set headers
    let config = {}
    if (headers.length > 0) {
      headers.forEach(({ key, value }) => {
        if (key) {
          objectPath.set(config, `headers.${key}`, value)
        }
      })
    }
    // send request
    let response = await httpClient
      .get(api, config)
      .then(({ data }) => {
        return { status: 200, data: data, message: 'success' }
      })
      .catch(function (error) {
        if (error.response) {
          return {
            status: error.response.status,
            message: error.response.data,
            data: null,
          }
        }
      })

    if (response?.status !== 200) {
      let propType = objectPath.get(designerPropsData, `propType`) || {}
      let filterProp = Object.keys(propType)
      filterProp.forEach((key) => {
        let [componentKey, dataKey] = key.split('/')
        if (propType[key] === 'formData') {
          objectPath.set(
            designerPropsData,
            `data.${componentKey}.${dataKey}`,
            {},
          )
        } else {
          objectPath.set(
            designerPropsData,
            `data.${componentKey}.${dataKey}`,
            '',
          )
        }
      })
      return response
    }
    let responseData = objectPath.get(response.data, dataKey)
    objectPath.set(datasourceValue, `${action.key}`, responseData)
    setDatasourceValue(datasourceValue)
    let internalSchema =
      objectPath.get(action, 'internalApi.schema.properties') || {}

    // set form data
    if (responseDataType === 'customData') {
      dataTable.forEach((data) => {
        let value = objectPath.get(responseData, data.response)
        let valueType = internalSchema[data.response]?.type
        if (value === undefined) {
          if (valueType === 'string') {
            value = ''
          } else if (valueType === 'array') {
            value = []
          } else if (valueType === 'object') {
            value = {}
          } else {
            value = null
          }
        }
        if (data.key === uischema.key) {
          if (!Array.isArray(value)) {
            value = Object.values(value)
          }
          setScoreList(value)
          console.log('fetch', value)
          setIsUpdateChunk(true)
        }
        if (data.key) {
          objectPath.set(formData, data.key, value)
        }
        if (data.response) {
          objectPath.set(formData, data.response, value)
        }
      })
      if (customName) {
        objectPath.set(formData, `props.${formData}`, responseData || {})
      }
    } else if (responseDataType === 'formData') {
      dataTable.forEach((data) => {
        let value = objectPath.get(responseData, data.response)
        let valueType = internalSchema[data.response]?.type
        if (value === undefined) {
          if (valueType === 'string') {
            value = ''
          } else if (valueType === 'array') {
            value = []
          } else if (valueType === 'object') {
            value = {}
          } else {
            value = null
          }
        }
        if (data.key === uischema.key) {
          if (!Array.isArray(value)) {
            value = Object.values(value)
          }
          setScoreList(value)
          console.log('fetch', value)
          setIsUpdateChunk(true)
        }
        if (data.key) {
          objectPath.set(formData, data.key, value)
        }
        if (data.response) {
          objectPath.set(formData, data.response, value)
        }
      })
    }
    response.data = formData
    objectPath.set(response, 'data', formData)
    return response
  }

  const postData = async (action, scores) => {
    // set api
    let api = setApi(action, formData, {
      allParams,
      designerData,
      datasourceValue,
    })
    if (!api) return {}
    // set headers
    let { headers = [] } = action
    let config = {}
    if (headers.length > 0) {
      headers.forEach(({ key, value }) => {
        if (key) {
          objectPath.set(config, `headers.${key}`, value)
        }
      })
    }
    let requestData = setRequestData(action, formData, scores)
    // send request
    let response = await httpClient
      .post(api, requestData, config)
      .then((data) => data)
      .catch(function (error) {
        if (error.response) {
          return {
            status: error.response.status,
            message: error.response.data,
            data: null,
          }
        }
      })
    return response
  }

  const putData = async (action, scores) => {
    // set api
    let api = setApi(action, formData, {
      allParams,
      designerData,
      datasourceValue,
    })
    if (!api) return {}
    // set headers
    let { headers = [] } = action
    let config = {}
    if (headers.length > 0) {
      headers.forEach(({ key, value }) => {
        if (key) {
          objectPath.set(config, `headers.${key}`, value)
        }
      })
    }
    let requestData = setRequestData(action, formData, scores)
    // send request
    let response = await httpClient
      .put(api, requestData, config)
      .then((data) => data)
      .catch(function (error) {
        if (error.response) {
          return {
            status: error.response.status,
            message: error.response.data,
            data: null,
          }
        }
      })

    return response
  }
  const patchData = async (action, scores) => {
    // set api
    let api = setApi(action, formData, {
      allParams,
      designerData,
      datasourceValue,
    })
    if (!api) return {}
    // set headers
    let { headers = [] } = action
    let config = {}
    if (headers.length > 0) {
      headers.forEach(({ key, value }) => {
        if (key) {
          objectPath.set(config, `headers.${key}`, value)
        }
      })
    }
    let requestData = setRequestData(action, formData, scores)
    // send request
    let response = await httpClient
      .patch(api, requestData, config)
      .then((data) => data)
      .catch(function (error) {
        if (error.response) {
          return {
            status: error.response.status,
            message: error.response.data,
            data: null,
          }
        }
      })

    return response
  }

  const deleteData = async (action, allParams) => {
    // set api
    let api = setApi(action, formData, {
      allParams,
      designerData,
      datasourceValue,
    })
    if (!api) return {}
    // set request body
    let { headers = [] } = action
    let config = {}
    if (headers.length > 0) {
      headers.forEach(({ key, value }) => {
        if (key) {
          objectPath.set(config, `headers.${key}`, value)
        }
      })
    }
    let response = await httpClient
      .delete(api, config)
      .then((data) => data)
      .catch(function (error) {
        if (error.response) {
          return {
            status: error.response.status,
            message: error.response.data,
            data: null,
          }
        }
      })
    return response
  }

  const callAPI = async (scores, action) => {
    let { apiGroup = [] } = action
    let promiseList = []
    apiGroup.forEach((item) => {
      let promise = new Promise(async (resolve, reject) => {
        let response = {}
        let data = null
        let status = null
        let message = ''
        if (item.method === 'get') {
          response = await getData(item)
        } else if (item.method === 'post') {
          response = await postData(item, scores)
        } else if (item.method === 'put') {
          response = await putData(item, scores)
        } else if (item.method === 'patch') {
          response = await patchData(item, scores)
        } else if (item.method === 'delete') {
          response = await deleteData(item, scores)
        }
        if (response) {
          data = response.data
          message = response.message
          status = response.status
        }

        if (status >= 400) {
          reject({
            status: status,
            data: null,
            message: message,
            api: item.api,
            apiName: item.apiName,
          })
        } else {
          resolve({
            status: status,
            data: data,
            message: message,
            api: item.api,
            apiName: item.apiName,
          })
        }
      })
        .then((response) => response)
        .catch((error) => error)
      promiseList.push(promise)
    })

    let response = await Promise.all(promiseList)

    if (action.isNotified) {
      if (Array.isArray(response)) {
        response.forEach((response = {}) => {
          let { status, data = null } = response
          if (status === null) return
          setNotification(action, response)
        })
      } else {
        let { status, data = null } = response || {}
        if (status === null) return
        setNotification(action, response)
      }
    }
    return response
  }

  const deleteAPI = async (scores, action) => {
    let { apiGroup = [] } = action
    let promiseList = []
    apiGroup.forEach((item) => {
      let promise = new Promise(async (resolve, reject) => {
        let response = {}
        if (item.method === 'delete') {
          let pathParams = item.pathParams || []
          let queryParams = item.queryParams || []
          let urlParams = pathParams.concat(queryParams)
          let scoreParamsKey = []
          let scoreParams = []
          urlParams.forEach(({ param, key, propType = '' }) => {
            if (propType === 'scoreChart') {
              key = key ?? param
              if (!scoreParamsKey.includes(key)) {
                scoreParamsKey.push(key)
              }
            }
          })
          if (scoreParamsKey.length > 0) {
            scores.forEach((score) => {
              let params = {}
              let hasParam = false
              scoreParamsKey.forEach((key) => {
                if (objectPath.has(score, key)) {
                  hasParam = true
                  let value = objectPath.get(score, key)
                  objectPath.set(params, key, value)
                }
              })
              if (hasParam) {
                scoreParams.push(params)
              }
            })
          }
          scoreParams.forEach(async (params) => {
            response = await deleteData(item, { ...allParams, ...params })
            if (response) {
              if (!action.isNotified || response.status === null) return
              setNotification(action, {
                status: response.status,
                data: response.status || null,
                message: response.status,
                api: item.api,
                apiName: item.apiName,
              })
              resolve(response)
            } else {
              reject(response)
            }
          })
        }
      })
        .then((response) => response)
        .catch((error) => error)
      promiseList.push(promise)
    })
    let response = await Promise.all(promiseList)
    return response
  }

  const setNotification = (afterAction, response) => {
    let {
      status,
      data = null,
      message = '',
      api = '',
      apiName = '',
    } = response || {}
    if (status >= 200 && status < 300) {
      let notice = PNotify.success({
        title: afterAction.notifySuccessTitle || 'Success',
        text: afterAction.notifySuccessText,
        icon: 'fas fa-check-circle fa-xl',
      })
      notice.on('click', function () {
        notice.close()
      })
      console.log(`API Name: ${apiName}`)
    } else {
      let notice = PNotify.error({
        title: afterAction.notifyErrorTitle || 'Error',
        text: JSON.stringify(message, null, 2) || afterAction.notifyErrorText,
        icon: 'fas fa-exclamation-circle fa-xl',
      })
      notice.on('click', function () {
        notice.close()
      })
      console.error(`Error API Name: ${apiName}`)
    }
  }

  const checkWarning = (index, scores) => {
    let warning = []
    let filterApiValidation = scoreConfig.filter(
      ({ validationType }) => validationType === 'api',
    )
    let filterCustomValidation = scoreConfig.filter(
      ({ validationType }) => validationType === 'custom',
    )
    filterApiValidation.forEach(({ name, api }) => {
      scores.forEach((score, index) => {
        let scoreValue = objectPath.get(score, name)
        let check = checkApiValidation(api, scoreValue)
        objectPath.set(warning, [index, name], check)
      })
    })
    filterCustomValidation.forEach(({ name, validation }) => {
      scores.forEach((score, index) => {
        let scoreValue = objectPath.get(score, name)
        let check = checkCustomValidation(validation, scoreValue)
        objectPath.set(warning, [index, name], check)
      })
    })
    if (index === -1) {
      objectPath.push(warningList, '', warning)
    } else {
      objectPath.set(warningList, index, warning)
    }
    setWarningList(warningList)
  }

  let scrollbarRef = useRef(null)
  let containerRef = useRef(null)
  const isAddScoreLast = scoreSetting?.addScorePosition === 'last'

  useEffect(() => {
    if (!scrollbarRef.current) return
    if (isAddScore && isAddScoreLast) {
      scrollbarRef.current.scrollLeft = scrollbarRef.current.scrollWidth
      return
    }
    scrollbarRef.current.scrollLeft = 0
  }, [isAddScore, isAddScoreLast])

  const [render, setRender] = useState(false)

  useEffect(() => {
    if (render) {
      setRender(false)
    }
  }, [render])

  useEffect(() => {
    if (!scoreChartValue) {
      if (!isReadOnly && scoreAction?.firstAuto) {
        let scoreList = []
        let score = checkFormatScore({})
        objectPath.push(scoreList, '', score)
        setScoreList(scoreList)
        updateScoreToFormData(scoreList)
        console.log('scoreAction?.firstAuto', {
          isReadOnly,
          firstAuto: scoreAction?.firstAuto,
          scoreChartValue,
        })
      } else {
        setScoreList([])
        checkScoreChunk([])
      }
    } else if (typeof scoreChartValue === 'object') {
      let array = scoreChartValue
      if (!Array.isArray(scoreChartValue)) {
        array = Object.values(scoreChartValue)
      }
      if (!isReadOnly && scoreAction?.firstAuto && array.length === 0) {
        let scoreList = []
        let score = checkFormatScore({})
        objectPath.push(scoreList, '', score)
        setScoreList(scoreList)
        updateScoreToFormData(scoreList)
      } else {
        setScoreList(array)
        checkScoreChunk(array)
      }
    }
  }, [scoreChartValue])

  const renderAddScore = () => (
    <AddScore
      fieldKey={fieldKey}
      uischema={scoreUischema}
      isAddScore={isAddScore}
      scoreIndex={-1}
      cancelScore={cancelScore}
      saveScore={saveScore}
      scoreList={scoreChunk}
      warningChart={warningChart}
    />
  )

  if (scoreType === 'createOnly') {
    return (
      <Form.Group className="mb-0" controlId={controlId}>
        <Row className="w-100 mx-0">
          <Col>
            <Label
              {...props}
              uischema={scoreUischema}
              className="font-weight-bold"
            />
          </Col>
        </Row>
        <Row className="p-2">
          <Col sm="12">
            <div className="d-flex">
              <div>
                {scoreConfig.map(({ group, label, data, enabled }) => {
                  if (!enabled) return
                  if (group) {
                    return (
                      <div class="storefront" style={scoreNameStyle}>
                        <div class="sign">
                          <span>{label}</span>
                        </div>
                        <div class="expander">
                          {data.map(({ label, enabled }) => {
                            if (!enabled) return
                            return (
                              <div className="head-box">
                                <span>{label}</span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  }
                  return (
                    <div className="head-container" style={scoreNameStyle}>
                      <div className="empty"></div>
                      <div className="head-box">
                        <span>{label}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
              <PerfectScrollbar
                containerRef={(ref) => {
                  scrollbarRef.current = ref
                }}
              >
                <div className="d-flex mb-3" ref={containerRef}>
                  <AddScore
                    fieldKey={fieldKey}
                    uischema={scoreUischema}
                    isAddScore={isAddScore}
                    scoreActionCreateOnly={scoreActionCreateOnly}
                    scoreActionCreate={scoreActionCreate}
                    scoreActionUpdate={scoreActionUpdate}
                    scoreIndex={-1}
                    cancelScore={cancelScore}
                    saveScore={saveScore}
                    scoreList={scoreChunk}
                    warningChart={warningChart}
                    isCreateOnly={true}
                  />
                </div>
              </PerfectScrollbar>
            </div>
          </Col>
        </Row>
      </Form.Group>
    )
  }

  return (
    <fieldset
      className="scheduler-border row"
      style={{
        padding: uischema.layoutPadding ?? '.5rem',
        margin: uischema.layoutMargin,
      }}
    >
      <legend className="scheduler-border">
        {!isReadOnly && (
          <Row className="w-100 mx-0">
            <Col className="d-flex">
              <Label
                {...props}
                uischema={scoreUischema}
                className="font-weight-bold"
              />
              {scoreActionCreate?.isEnabled && (
                <Button
                  outline
                  variant="outline-primary"
                  className="py-0 px-1 mx-2 d-flex align-items-center"
                  disabled={isAddScore || isEditScore !== null}
                  onClick={() => addScore()}
                >
                  <FeatherIcon icon="plus" size="14" />
                </Button>
              )}
            </Col>
          </Row>
        )}
      </legend>
      <div className="w-100">
        <Form.Group className="mb-0" controlId={controlId}>
          <Row className="p-2">
            <Col sm="12" className="px-0">
              <div className="d-flex">
                <div>
                  {scoreConfig.map(({ group, label, data, enabled }) => {
                    if (!enabled) return
                    if (group) {
                      return (
                        <div class="storefront" style={scoreNameStyle}>
                          <div class="sign">
                            <span>{label}</span>
                          </div>
                          <div class="expander">
                            {data.map(({ label, enabled }) => {
                              if (!enabled) return
                              return (
                                <div className="head-box">
                                  <span>{label}</span>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    }
                    return (
                      <div className="head-container" style={scoreNameStyle}>
                        <div className="empty"></div>
                        <div className="head-box">
                          <span>{label}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>

                <PerfectScrollbar
                  containerRef={(ref) => {
                    scrollbarRef.current = ref
                  }}
                >
                  {editOnRow ? (
                    <div className="d-flex mb-3" ref={containerRef}>
                      {scoreChunk &&
                        scoreChunk.map((score, index) => {
                          return (
                            <EditRowScore
                              fieldKey={fieldKey}
                              uischema={scoreUischema}
                              isAddScore={isAddScore}
                              score={score}
                              scoreIndex={index}
                              cancelScore={cancelScore}
                              saveScore={saveScore}
                              deleteScore={deleteScore}
                              scoreList={scoreChunk}
                              warningChart={warningChart}
                              isReadOnly={isReadOnly}
                            />
                          )
                        })}
                    </div>
                  ) : (
                    <div className="d-flex mb-3" ref={containerRef}>
                      {isAddScore && !isAddScoreLast && renderAddScore()}
                      {scoreChunk &&
                        scoreChunk.map((score, index) => {
                          let warning = objectPath.get(warningList, index) || []
                          if (index === isEditScore) {
                            return (
                              <AddScore
                                fieldKey={fieldKey}
                                uischema={scoreUischema}
                                isAddScore={isEditScore}
                                scoreIndex={index}
                                cancelScore={cancelScore}
                                saveScore={saveScore}
                                scoreList={scoreChunk}
                                warningChart={warningChart}
                              />
                            )
                          }
                          return (
                            <ShowScore
                              fieldKey={fieldKey}
                              uischema={scoreUischema}
                              score={score}
                              scoreIndex={index}
                              editScore={editScore}
                              deleteScore={deleteScore}
                              warningChart={warningChart}
                              isReadOnly={isReadOnly}
                            />
                          )
                        })}
                      {isAddScore && isAddScoreLast && renderAddScore()}
                    </div>
                  )}
                </PerfectScrollbar>
              </div>
            </Col>
          </Row>
          {count > 0 && (
            <Row className="d-flex justify-content-end mb-2">
              <Pagination
                count={count}
                variant="outlined"
                page={page}
                onChange={(e, value) => setPage(value)}
              />
            </Row>
          )}
        </Form.Group>
      </div>
    </fieldset>
  )
}

export default SingleScore
